import React, { useEffect, useState } from "react";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { startAuthentication } from "@simplewebauthn/browser";

import {
  Box,
  Button,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowLeftIcon,
  ErrorIcon,
} from "@braintrust/braintrust-ui-components/Icons";

export const WEBAUTHN_ASSERTION_ERROR_MESSAGE = {
  CANCELLED:
    "Passkey verification was cancelled or timed out. If your passkey manager didn't open, make sure it's enabled in your browser and try again.",
  BROWSER: "Your browser couldn't use your passkey. Please try again.",
  SERVER:
    "We couldn't verify your passkey. Please refresh the page and try again.",
} as const;

// The browser reports a dismissed prompt, a timeout and "no matching passkey offered" all as
// NotAllowedError, so they can't be told apart any further.
const CANCELLED_ERROR_NAMES = ["NotAllowedError", "AbortError"];

const getBrowserErrorMessage = (browserError: unknown) => {
  if (
    browserError instanceof Error &&
    CANCELLED_ERROR_NAMES.includes(browserError.name)
  ) {
    return WEBAUTHN_ASSERTION_ERROR_MESSAGE.CANCELLED;
  }

  return WEBAUTHN_ASSERTION_ERROR_MESSAGE.BROWSER;
};

type UseWebAuthnAssertionParams<TCompleteResult> = {
  begin: () => Promise<PublicKeyCredentialRequestOptionsJSON>;
  complete: (assertion: AuthenticationResponseJSON) => Promise<TCompleteResult>;
};

export const useWebAuthnAssertion = <TCompleteResult,>({
  begin,
  complete,
}: UseWebAuthnAssertionParams<TCompleteResult>) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authenticate = async (): Promise<TCompleteResult | undefined> => {
    setIsAuthenticating(true);
    setError(null);
    try {
      const options = await begin();
      const assertion = await startAuthentication({
        optionsJSON: options,
      }).catch((browserError: unknown) => {
        setError(getBrowserErrorMessage(browserError));
        return undefined;
      });
      if (!assertion) {
        return undefined;
      }

      return await complete(assertion);
    } catch (_e) {
      setError(WEBAUTHN_ASSERTION_ERROR_MESSAGE.SERVER);
      return undefined;
    } finally {
      setIsAuthenticating(false);
    }
  };

  return { authenticate, isAuthenticating, error };
};

type WebAuthnAssertionPromptProps = {
  title?: string;
  description?: string;
  error: string | null;
  isAuthenticating: boolean;
  onAuthenticate: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
  autoStart?: boolean;
};

export const WebAuthnAssertionPrompt = ({
  title = "2-step authentication",
  description = "Verify your identity using passkey.",
  error,
  isAuthenticating,
  onAuthenticate,
  onCancel,
  cancelLabel = "Cancel",
  autoStart = false,
}: WebAuthnAssertionPromptProps) => {
  useEffect(() => {
    if (autoStart) {
      onAuthenticate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Typography
        component="h4"
        variant="title"
        fontWeight={400}
        sx={{ mb: 2 }}
      >
        {title}
      </Typography>

      {!!error ? (
        <Typography component="p" color="negative" fontWeight={500}>
          <ErrorIcon
            sx={{
              color: "var(--negative-1)",
              fontSize: "20px",
              verticalAlign: "sub",
              mr: 0.75,
            }}
          />
          {error}
        </Typography>
      ) : (
        <Typography component="p">{description}</Typography>
      )}
      <Stack sx={{ gap: 2, mt: 4 }}>
        <Button
          variant="dark-violet"
          shape="squared"
          fullWidth
          onClick={onAuthenticate}
          disabled={isAuthenticating}
          disabledType="no-change"
          sx={{
            boxShadow: "0px 4px 12px 0px #5D197440 !important",
            svg: { color: "var(--white)" },
          }}
          endIcon={isAuthenticating ? <Loader size={20} /> : undefined}
        >
          {isAuthenticating ? "Authenticating…" : "Authenticate using passkey"}
        </Button>
        {!!onCancel && (
          <Button
            variant="secondary"
            shape="squared"
            fullWidth
            startIcon={<ArrowLeftIcon />}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
